// @file Surface drafts store
import { isAppUsing } from '@@/bits/flip'
import { getVuexStore } from '@@/bits/pinia'
import { addDraftCidIfAbsent } from '@@/bits/post_drafts'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import PadletApi from '@@/surface/padlet_api'
import { DraftSaveState } from '@@/vuexstore/modules/draft'
import { sortBy } from 'lodash-es'
import { defineStore, storeToRefs } from 'pinia'
import type { Cid, DraftPost, Id, Post, PostAttributes } from 'types'
import { computed } from 'vue'
import type { RootState } from 'vuexstore/surface/types'

export const useSurfaceDraftsStore = defineStore('surfaceDrafts', () => {
  const surfaceVuexStore = getVuexStore<RootState>()
  const surfacePostsStore = useSurfacePostsStore()
  const { wallId } = storeToRefs(useSurfaceStore())

  // getter functions
  function isCidBeingDrafted(cid: Cid): boolean {
    return surfaceVuexStore?.getters['draft/isCidBeingDrafted']?.(cid) ?? false
  }
  function getDraftFromCid(cid: Cid): DraftPost | null {
    return surfaceVuexStore?.getters['draft/draftByCid']?.(cid) ?? null
  }

  // Note: this isn't same as the getter of the same name in vuex drafts; that is an uncached vuex getter function
  const draftByCid = computed<Record<Cid, DraftPost>>(() => surfaceVuexStore?.state.draft.draftByCid ?? {})
  const activeDraftCid = computed<Cid | null>(() => activeDraft.value?.cid ?? null)
  const draftById = computed<Record<Id, DraftPost>>(() =>
    Object.values(draftByCid.value).reduce<Record<Id, DraftPost>>((accumm, draft) => {
      if (draft.id != null) {
        accumm[draft.id] = draft
      }
      return accumm
    }, {}),
  )

  const activeDraft = computed<DraftPost | null>(() =>
    surfacePostsStore.postBeingEditedCid != null ? draftByCid.value[surfacePostsStore.postBeingEditedCid] : null,
  )

  const draftCids = computed<Cid[]>(() => {
    return isAppUsing('postDrafts')
      ? sortBy(
          // new: sort by created_at
          Object.keys(draftByCid.value),
          (postCid) => -1 * new Date(draftByCid.value[postCid].created_at ?? 0).valueOf(),
        )
      : surfaceVuexStore?.getters['draft/draftCids'] // previously: sorted by draftCreatedAt
  })

  function isCidActiveDraft(cid: Cid): boolean {
    return surfaceVuexStore?.getters?.['draft/isCidActiveDraft']?.({ cid })
  }

  const isActiveDraftNewPost = computed<boolean>(() =>
    activeDraft.value == null ? false : !surfacePostsStore.isExistingPost(activeDraft.value.cid),
  )

  // actions
  function startEditingDraft(draftCid: Cid): void {
    // startEditingPost works on both drafts and posts, depending on their cid
    // if cid is draft, it is set as the active draft. if post, it copies an existing post and sets it to the active draft.
    void surfaceVuexStore?.dispatch('post/startEditingPost', { postCid: draftCid })
  }

  // stop editing draft, but doesn't discard it. for eg when minimizing composer
  function stopEditingDraft(): void {
    void surfaceVuexStore?.dispatch('post/stopEditingPost')
  }

  // handler for when a user clicks "close" on the composer. discards draft currently being edited
  async function cancelEditingDraft(): Promise<void> {
    if (activeDraftCid.value == null) return
    await removeDraft(activeDraftCid.value)
  }

  // set the title for minimized draft to "Draft 1", "Draft 2", ... if necessary
  function setDraftNumber(draftCid: Cid): void {
    void surfaceVuexStore?.dispatch('draft/setDraftNumber', { cid: draftCid })
  }

  // Fetches list of drafts on the wall and inserts into vuex store
  async function fetchPostDrafts(): Promise<void> {
    const postDrafts = await PadletApi.WishDraft.list({ wallId: wallId.value })

    for (const postDraft of postDrafts) {
      insertDraft(postDraft)
    }
    void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState') // mobile app needs knowledge of CIDs that surface has assigned to drafts
  }

  // Inserts new draft. doesn't trigger autosave (use draft/updateDraft for that)
  function insertDraft(newDraft: DraftPost): void {
    newDraft = addDraftCidIfAbsent(newDraft)
    newDraft.draftCreatedAt = Date.now()
    void surfaceVuexStore?.commit('draft/SET_DRAFT_AUTOSAVE_QUEUE', newDraft.cid)
    void surfaceVuexStore?.commit('draft/SET_DRAFT_SYNC_STATE', {
      cid: newDraft.cid,
      syncState: {
        state: newDraft.id == null ? DraftSaveState.Unsaved : DraftSaveState.Saved,
        loading: false,
        error: false,
      },
    })
    void surfaceVuexStore?.commit('draft/UPDATE_DRAFT', newDraft)
    setDraftNumber(newDraft.cid)
  }

  // wraps vuex removeDraft function. Removes a draft given its cid.
  // Calls delete draft API.
  // Optionally removes corresponding post
  async function removeDraft(cid: Cid): Promise<void> {
    if (!surfacePostsStore.isExistingPost(cid)) {
      // remove draft that's not saved as post yet -> remove corresponding entity in posts store.
      await surfaceVuexStore?.dispatch('post/deletePost', { postCid: cid })
    }
    await surfaceVuexStore?.dispatch('draft/removeDraft', cid)
  }

  async function removeAllPostDrafts(): Promise<void> {
    await Promise.all(draftCids.value.map(removeDraft))
  }

  async function publishAllPostDrafts(): Promise<void> {
    // reverse to publish the oldest draft first
    // need to reverse on a separate line: https://sonarcloud.io/organizations/padlet/rules?open=typescript%3AS4043&rule_key=typescript%3AS4043
    const reversedDraftCids = [...draftCids.value].reverse()
    reversedDraftCids.forEach((cid) => {
      void surfaceVuexStore?.dispatch('draft/publishDraft', { cid })
    })
  }

  function setDraftScheduledAt(cid: Cid, scheduledAt: string): void {
    void surfaceVuexStore?.dispatch('draft/updateDraft', {
      cid,
      scheduled_at: new Date(scheduledAt).toISOString(),
    })
  }

  function removeDraftScheduledAt(cid: Cid): void {
    void surfaceVuexStore?.dispatch('draft/updateDraft', {
      cid,
      scheduled_at: null,
    })
  }

  function stopUploadFileInDraft(cid: Cid): void {
    void surfaceVuexStore?.dispatch('draft/stopUploadFileInDraft', { cid })
  }

  function stopAutoPublishDraft(cid: Cid): void {
    void surfaceVuexStore?.dispatch('draft/stopAutoPublishDraft', { cid })
  }

  function updateDraft(post: Post | DraftPost | PostAttributes): void {
    void surfaceVuexStore?.dispatch('draft/updateDraft', post)
  }

  function insertDraftRemote(newDraft: DraftPost): void {
    if (newDraft.id == null) return // a saved draft should always have an ID

    const existingDraft = draftById.value[newDraft.id]
    if (existingDraft != null) return // if draft with id already exists, no need to insert

    insertDraft(newDraft)

    void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState')
  }

  function updateDraftRemote(draft: DraftPost): void {
    if (draft.id == null) return

    const existingDraft = draftById.value[draft.id]
    if (existingDraft != null) {
      // if draft with id already exists, update it
      const updatedDraft = { ...existingDraft, ...draft }
      void surfaceVuexStore?.commit('draft/UPDATE_DRAFT', updatedDraft)
    } else {
      // the draft doesn't exist in store yet; insert it instead.
      insertDraft(draft)
    }

    void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState')
  }

  function removeDraftRemote(draft: Pick<DraftPost, 'id'>): void {
    if (draft.id == null) return

    const existingDraft = draftById.value[draft.id]
    if (existingDraft != null) {
      void surfaceVuexStore?.commit('draft/REMOVE_DRAFT', existingDraft.cid)
    }

    void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState')
  }

  return {
    // getter functions
    isCidBeingDrafted,
    getDraftFromCid,
    isCidActiveDraft,

    draftByCid,
    draftById,
    activeDraftCid,
    activeDraft,
    draftCids,
    isActiveDraftNewPost,
    // actions
    startEditingDraft,
    stopEditingDraft,
    cancelEditingDraft,
    insertDraft,
    updateDraft,
    removeDraft,
    fetchPostDrafts,
    removeAllPostDrafts,
    publishAllPostDrafts,
    setDraftScheduledAt,
    removeDraftScheduledAt,
    stopUploadFileInDraft,
    stopAutoPublishDraft,
    insertDraftRemote,
    updateDraftRemote,
    removeDraftRemote,
  }
})
